import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button } from "@common";
import { formatMoney } from "@utils";
import { nonBankingReason } from "@utils/ontarioBankingDays";
import CurrencyBadge from "./CurrencyBadge";
import { WEEKDAY_SPLIT_INTERVALS, occurrencesInRange } from "./scheduleOccurrences";

// ─── option lists (mirror backend apps/schedule_payment choices) ──────────────

const EXPENSE_TYPE_OPTIONS = [
  { value: "rent", label: "Rent" },
  { value: "loan", label: "Loan" },
  { value: "interest", label: "Interest" },
  { value: "payroll", label: "Payroll" },
  { value: "other", label: "Other" },
];

const KNOWN_EXPENSE_TYPES = new Set(
  EXPENSE_TYPE_OPTIONS.map((o) => o.value).filter((v) => v !== "other"),
);

const PAYMENT_TYPE_OPTIONS = [
  { value: "direct_deposit", label: "EFT / Direct Deposit" },
  { value: "cheque", label: "Cheque" },
  { value: "wire_transfer", label: "Wire Transfer" },
  { value: "credit_card", label: "Credit Card" },
  { value: "cash", label: "Cash" },
  { value: "pre_authorized_debit", label: "Pre-Authorized Debit" },
  { value: "other", label: "Other" },
];

const KNOWN_PAYMENT_TYPES = new Set(
  PAYMENT_TYPE_OPTIONS.map((o) => o.value).filter((v) => v !== "other"),
);

const PAYMENT_INTERVAL_OPTIONS = [
  { value: "one_time", label: "One-time" },
  // Mon-Fri, never a weekend — the label says so because "Daily" alone reads as
  // all seven days, and the difference is 30% of the month's outflow.
  { value: "daily", label: "Daily (Mon-Fri)" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "semi_monthly", label: "Semi-monthly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annually", label: "Semi-annually" },
  { value: "annually", label: "Annually" },
];

// Two states and one ACTION. Delete is offered alongside them because that is
// where people look for it, but it is not a status: nothing is ever stored as
// "deleted", the row is removed. Keeping it out of the enum is what stops it
// becoming a soft delete that every query then has to remember to filter.
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
];

const DELETE_ACTION = "__delete__";

// ISO weekdays (1=Mon … 5=Fri), matching what the backend stores. Saturday and
// Sunday are absent BY DESIGN rather than disabled: money does not move on a
// weekend, so there is no state in which picking one is meaningful.
const WEEKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
];

// How far ahead the resulting-payments preview looks. Eight weeks shows three
// cycles of a biweekly split, which is enough to see the pattern repeat.
const PREVIEW_DAYS = 56;
const PREVIEW_ROWS = 8;

const EMPTY_FORM = {
  bank_account: "",
  expense_type: "rent",
  expense_type_other: "",
  payment_type: "direct_deposit",
  payment_type_other: "",
  payment_interval: "monthly",
  payment_weekdays: [],
  amount: "",
  start_date: "",
  end_date: "",
  notes: "",
  status: "active",
};

function addDaysISO(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

// Local calendar day, not `toISOString()` — that is UTC, and any evening in
// Ontario it names tomorrow. A cancellation dated a day late leaves one extra
// payment on the grid.
function todayISO() {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${mm}-${dd}`;
}

function fmtPreviewDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// ─── field primitives (styled with tailwind.config tokens only) ───────────────

const FIELD_CLS =
  "w-full rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-3.5 py-2.5 text-sm text-palette-gray-primary placeholder:text-palette-gray-placeholder focus:border-palette-indigo-ring focus:outline-none focus:ring-2 focus:ring-palette-indigo-border transition-colors";

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-sm font-medium text-palette-gray-heading">{children}</label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${FIELD_CLS} appearance-none pr-9`}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-palette-gray-secondary"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

/**
 * Single-select account picker that renders TWO lines per option — clinic on
 * top, account underneath.
 *
 * A native <select> can't do this: `<option>` renders as a single text run, so
 * the clinic and the account had to be crushed into one "Clinic — Account (CAD)"
 * string. This is a custom listbox instead.
 *
 * The panel is absolutely positioned and would be clipped by the modal body's
 * `overflow-y-auto`, which is survivable only because this is the FIRST field in
 * the form — there is always room below it. Move this field down the form and it
 * needs a portal.
 */
function AccountSelectField({ label, value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Reset the search each time it reopens, so a stale query can't make the list
  // look empty.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // String compare: `value` arrives as a number when editing an existing
  // payment, but as a string from a fresh selection.
  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.ownerName, o.clinicCode, o.accountLabel].some((f) => f?.toLowerCase().includes(q)),
    );
  }, [options, query]);

  return (
    <div>
      <Label>{label}</Label>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          // min-h holds the two-line height even while the placeholder (one
          // line) is showing, so picking an account doesn't jolt the form.
          className={`${FIELD_CLS} flex min-h-[3.5rem] items-center justify-between gap-2 text-left`}
        >
          {selected ? (
            <span className="min-w-0">
              <span className="block truncate text-xs text-palette-gray-secondary">
                {selected.ownerName}
              </span>
              <span className="block truncate font-medium text-palette-gray-primary">
                {selected.accountLabel}
              </span>
            </span>
          ) : (
            <span className="text-palette-gray-placeholder">{placeholder}</span>
          )}
          <svg
            className={`h-3.5 w-3.5 shrink-0 text-palette-gray-secondary transition-transform ${
              open ? "rotate-180" : ""
            }`}
            viewBox="0 0 10 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-palette-gray-divider bg-white shadow-xl">
            <div className="border-b border-palette-gray-divider p-2">
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${options.length} accounts…`}
                className="w-full rounded-lg border border-palette-gray-divider px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-palette-indigo-label"
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-palette-gray-placeholder">
                  No matches.
                </div>
              ) : (
                filtered.map((o) => {
                  const active = String(o.value) === String(value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                      className={`block w-full px-4 py-2.5 text-left transition-colors ${
                        active ? "bg-palette-indigo-soft" : "hover:bg-palette-gray-subtle"
                      }`}
                    >
                      <span className="block truncate text-xs text-palette-gray-secondary">
                        {o.ownerName}
                      </span>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-sm font-medium text-palette-gray-primary">
                          {o.accountLabel}
                        </span>
                        {o.currency && <CurrencyBadge currency={o.currency} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DateField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={FIELD_CLS}
      />
    </div>
  );
}

// ─── modal ────────────────────────────────────────────────────────────────────

export default function SchedulePaymentModal({
  isOpen,
  onClose,
  onSave,
  // Active bank accounts, already filtered by the caller. A payment is drawn
  // from an account now, not a clinic — a clinic can own several.
  accountOptions = [],
  clinicOptions = [],
  // What the account picker's first line names: the owning clinic (default) or
  // the owning entity, on the Dens N Dente page where accounts have no clinic.
  groupBy = "clinic",
  initialValues = null,
  saving = false,
  isEdit = false,
  // Hard delete. Omitted by callers that cannot delete (or on create, where
  // there is nothing to remove yet), and the Delete choice hides with it.
  onDelete,
  deleting = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  // Two-step, always: deleting takes the payment's whole history with it, and a
  // single mis-click on a status pill must not be able to do that.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Owner and account are kept as SEPARATE fields, not pre-joined into one
  // string — AccountSelectField renders them on two lines. Sorted by owner to
  // match how the list page groups.
  const accountSelectOptions = useMemo(() => {
    const byEntity = groupBy === "entity";
    const nameByCode = new Map(clinicOptions.map((o) => [o.value, o.name ?? o.label]));
    // Clinic line reads "Name - CODE". `name` falls back to the code when a
    // clinic has no name, so only append the code when it adds something —
    // otherwise the line would read "ON-S-ARD - ON-S-ARD".
    const clinicOwner = (code) => {
      const name = nameByCode.get(code);
      return name && name !== code ? `${name} - ${code}` : code;
    };
    const ownerOf = (a) =>
      byEntity
        ? (a.entityName ?? "Entity account")
        : a.clinicCode
          ? clinicOwner(a.clinicCode)
          : "Entity account";
    return [...accountOptions]
      .map((a) => ({
        value: a.value,
        clinicCode: a.clinicCode,
        ownerName: ownerOf(a),
        accountLabel: a.label,
        currency: a.currency,
      }))
      .sort(
        (a, b) =>
          a.ownerName.localeCompare(b.ownerName) ||
          String(a.accountLabel).localeCompare(String(b.accountLabel)),
      );
  }, [accountOptions, clinicOptions, groupBy]);

  useEffect(() => {
    if (isOpen) {
      const init = { ...EMPTY_FORM, ...(initialValues ?? {}) };
      // If editing a schedule whose expense_type / payment_type is a custom
      // value, surface it as "Other" with the text pre-filled.
      if (init.expense_type && !KNOWN_EXPENSE_TYPES.has(init.expense_type)) {
        init.expense_type_other = init.expense_type;
        init.expense_type = "other";
      }
      if (init.payment_type && !KNOWN_PAYMENT_TYPES.has(init.payment_type)) {
        init.payment_type_other = init.payment_type;
        init.payment_type = "other";
      }
      setForm(init);
      setError(null);
      setConfirmingDelete(false);
    }
  }, [isOpen, initialValues]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  // Weekday split — weekly and biweekly only. Anything else keeps the original
  // rule, where the schedule falls on start_date's own weekday.
  const supportsWeekdays = WEEKDAY_SPLIT_INTERVALS.has(form.payment_interval);
  const weekdays = useMemo(
    () => (supportsWeekdays ? [...(form.payment_weekdays ?? [])].sort((a, b) => a - b) : []),
    [supportsWeekdays, form.payment_weekdays],
  );

  function toggleWeekday(day) {
    setForm((f) => {
      const current = new Set(f.payment_weekdays ?? []);
      current.has(day) ? current.delete(day) : current.add(day);
      return { ...f, payment_weekdays: [...current].sort((a, b) => a - b) };
    });
  }

  const amountNum = Number(form.amount) || 0;
  const perDay = weekdays.length ? amountNum / weekdays.length : amountNum;

  // Built from the SAME generator the cash-flow grid uses, so the preview cannot
  // drift from where the payments actually land.
  const preview = useMemo(() => {
    if (!form.start_date) return [];
    return occurrencesInRange(
      {
        payment_interval: form.payment_interval,
        payment_weekdays: weekdays,
        start_date: form.start_date,
        end_date: form.end_date || null,
        amount: amountNum,
        exceptions: [],
      },
      form.start_date,
      addDaysISO(form.start_date, PREVIEW_DAYS),
    );
  }, [form.payment_interval, form.start_date, form.end_date, amountNum, weekdays]);

  // Holidays are ALLOWED here — a payment can be booked on one, it just will not
  // clear that day — so this warns instead of blocking. Weekends are caught by
  // the same check: the weekday picker cannot produce one, but a schedule with no
  // split still inherits start_date's weekday, which may be a Saturday.
  const closureWarnings = useMemo(() => {
    const seen = new Map();
    for (const occ of preview) {
      const [y, m, d] = occ.date.split("-").map(Number);
      const reason = nonBankingReason(new Date(y, m - 1, d));
      if (reason && !seen.has(occ.date)) seen.set(occ.date, reason);
    }
    return [...seen.entries()].map(([date, reason]) => ({ date, reason }));
  }, [preview]);

  // Occurrences a closure pushed off their rule date. Reported rather than left
  // silent: the payment is landing on a day the user did not pick, and seeing
  // "Canada Day → Jun 30" is the difference between a rule and a surprise.
  const movedPayments = useMemo(
    () =>
      preview
        .filter((occ) => occ.movedFrom)
        .map((occ) => {
          const [y, m, d] = occ.movedFrom.split("-").map(Number);
          return {
            to: occ.date,
            from: occ.movedFrom,
            reason: nonBankingReason(new Date(y, m - 1, d)),
          };
        }),
    [preview],
  );

  const cancelled = form.status === "cancelled";

  /**
   * Status pills. Picking "Cancelled" seeds the stop date with today rather
   * than leaving it blank — the overwhelmingly common case is "stop this as of
   * now", and an empty required field would make the routine action the fiddly
   * one. An existing end date is kept: a series already ending Dec 31 that gets
   * cancelled did not suddenly end today.
   */
  function chooseStatus(value) {
    setError(null);
    if (value === DELETE_ACTION) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    setForm((f) => ({
      ...f,
      status: value,
      end_date: value === "cancelled" && !f.end_date ? todayISO() : f.end_date,
    }));
  }

  // Occurrences that disappear from the grid if this cancellation date sticks —
  // counted from the CURRENT window backwards, so backdating a cancellation
  // over payments that already happened says so instead of quietly dropping
  // them. Cheap: the same generator the preview uses, over the affected span.
  const droppedByCancel = useMemo(() => {
    if (!cancelled || !form.end_date || !form.start_date) return 0;
    if (form.end_date >= todayISO()) return 0; // future/today: nothing paid is at risk
    return occurrencesInRange(
      {
        payment_interval: form.payment_interval,
        payment_weekdays: weekdays,
        start_date: form.start_date,
        end_date: null,
        amount: amountNum,
        exceptions: [],
      },
      addDaysISO(form.end_date, 1),
      todayISO(),
    ).length;
  }, [cancelled, form.end_date, form.start_date, form.payment_interval, weekdays, amountNum]);

  function handleSave() {
    if (cancelled && !form.end_date) {
      setError("Pick the date this payment stops. Payments up to that date stay on the cash flow.");
      return;
    }
    // A series cancelled before it ever ran has no occurrences at all, which is
    // not a cancellation — it is a record that should not exist. Say so rather
    // than letting the backend's start/end rule answer with a field error.
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      setError(
        `This stops the payment before it starts (${fmtPreviewDate(form.start_date)}), so it ` +
          "would leave no payments at all. Delete the record instead.",
      );
      return;
    }
    setError(null);
    const resolved = { ...form };
    if (resolved.expense_type === "other") {
      resolved.expense_type = (resolved.expense_type_other || "").trim();
    }
    if (resolved.payment_type === "other") {
      resolved.payment_type = (resolved.payment_type_other || "").trim();
    }
    // Sent explicitly rather than left stale: switching a Tue/Thu schedule to
    // monthly must drop the selection, not carry it silently in the column.
    resolved.payment_weekdays = supportsWeekdays ? weekdays : [];
    delete resolved.expense_type_other;
    delete resolved.payment_type_other;
    onSave?.(resolved);
  }

  const footer = (
    <>
      <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSave}
        disabled={saving}
        className="font-semibold"
      >
        {saving ? "Saving…" : isEdit ? "Save changes" : "Save payment"}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit scheduled payment" : "Schedule a payment"}
      subtitle="Tier 2 — admin-managed scheduled payment"
      size="lg"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Bank account — clinic on line 1, account on line 2 */}
        <AccountSelectField
          label="Bank account"
          value={form.bank_account}
          onChange={set("bank_account")}
          options={accountSelectOptions}
          placeholder="Select an account"
        />

        {/* Expense type / Payment type */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Expense type"
            value={form.expense_type}
            onChange={set("expense_type")}
            options={EXPENSE_TYPE_OPTIONS}
          />
          <SelectField
            label="Payment type"
            value={form.payment_type}
            onChange={set("payment_type")}
            options={PAYMENT_TYPE_OPTIONS}
          />
        </div>

        {/* Custom expense type / payment type (shown when "Other" is selected) */}
        {(form.expense_type === "other" || form.payment_type === "other") && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {form.expense_type === "other" && (
              <div>
                <Label>Specify expense type</Label>
                <input
                  type="text"
                  value={form.expense_type_other}
                  onChange={(e) => set("expense_type_other")(e.target.value)}
                  placeholder="e.g. Marketing, Equipment lease…"
                  className={FIELD_CLS}
                />
              </div>
            )}
            {form.payment_type === "other" && (
              <div>
                <Label>Specify payment type</Label>
                <input
                  type="text"
                  value={form.payment_type_other}
                  onChange={(e) => set("payment_type_other")(e.target.value)}
                  placeholder="e.g. E-transfer, Money order…"
                  className={FIELD_CLS}
                />
              </div>
            )}
          </div>
        )}

        {/* Payment interval / Amount */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Payment interval"
            value={form.payment_interval}
            onChange={set("payment_interval")}
            options={PAYMENT_INTERVAL_OPTIONS}
          />
          <div>
            <Label>Amount (CAD)</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-palette-gray-placeholder">
                $
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => set("amount")(e.target.value)}
                placeholder="0.00"
                className={`${FIELD_CLS} pl-7 tabular-nums`}
              />
            </div>
          </div>
        </div>

        {/* Weekday split — weekly / biweekly only */}
        {supportsWeekdays && (
          <div>
            <Label>
              Split across weekdays{" "}
              <span className="font-normal text-palette-gray-secondary">(optional)</span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_OPTIONS.map((d) => {
                const selected = weekdays.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleWeekday(d.value)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                      selected
                        ? "border-palette-indigo-border bg-palette-indigo-soft text-palette-indigo-text"
                        : "border-palette-gray-divider bg-white text-palette-gray-secondary hover:bg-palette-gray-subtle"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        selected ? "bg-palette-indigo-badge" : "bg-palette-gray-placeholder"
                      }`}
                    />
                    {d.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-palette-gray-secondary">
              {weekdays.length === 0 ? (
                <>
                  None selected — the payment falls on the start date&apos;s own weekday, once
                  {form.payment_interval === "biweekly" ? " every two weeks" : " a week"}.
                </>
              ) : (
                <>
                  {formatMoney(amountNum)} per {form.payment_interval === "biweekly" ? "2 " : ""}
                  week, split {formatMoney(perDay)} × {weekdays.length}{" "}
                  {weekdays.length === 1 ? "day" : "days"}. Weekends are not offered, and a day
                  landing on an Ontario bank holiday moves to the nearest weekday the bank is open.
                </>
              )}
            </p>
          </div>
        )}

        {/* Start date / End date. The end date moves down into the status block
            when cancelled — same column either way, but two inputs bound to one
            value on screen at once would read as two different dates. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField label="Start date" value={form.start_date} onChange={set("start_date")} />
          {!cancelled && (
            <DateField
              label="End date (optional)"
              value={form.end_date}
              onChange={set("end_date")}
              placeholder="Ongoing"
            />
          )}
        </div>

        {/* Reactivating leaves the old stop date in place, and it would silently
            keep the series stopped. Said out loud, with the fix named. */}
        {!cancelled && form.end_date && form.end_date < todayISO() && (
          <p className="text-sm text-palette-amber-primary">
            This payment already ended on {fmtPreviewDate(form.end_date)}, so it produces nothing
            going forward. Clear the end date to start it up again.
          </p>
        )}

        {/* Holiday moves — informational, not a problem: the schedule handled it.
            Shown so a date the user did not pick is never a surprise. */}
        {movedPayments.length > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-palette-indigo-border bg-palette-indigo-soft px-4 py-3 text-sm text-palette-indigo-text">
            <svg
              className="mt-0.5 h-4 w-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M2 8h9M8 5l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="8" cy="8" r="6.5" />
            </svg>
            <div>
              <p className="font-medium">
                {movedPayments.length}{" "}
                {movedPayments.length === 1 ? "payment was" : "payments were"} moved off an Ontario
                bank holiday.
              </p>
              <ul className="mt-1 space-y-0.5">
                {movedPayments.map((m) => (
                  <li key={m.from}>
                    {fmtPreviewDate(m.from)} ({m.reason}) → {fmtPreviewDate(m.to)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Anything STILL on a closed day. The weekday split moves off holidays
            automatically, so this only catches a schedule with no split, whose
            date comes from start_date's own weekday and may be a weekend. */}
        {closureWarnings.length > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-palette-amber-border bg-palette-amber-soft px-4 py-3 text-sm text-palette-amber-primary">
            <svg
              className="mt-0.5 h-4 w-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M8 1.75l6.25 11.5H1.75L8 1.75zM8 6.25v3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            <div>
              <p className="font-medium">
                {closureWarnings.length} upcoming{" "}
                {closureWarnings.length === 1 ? "payment lands" : "payments land"} on a day the bank
                does not move money.
              </p>
              <ul className="mt-1 space-y-0.5">
                {closureWarnings.map((w) => (
                  <li key={w.date}>
                    {fmtPreviewDate(w.date)} — {w.reason}
                  </li>
                ))}
              </ul>
              <p className="mt-1">
                Saving is still allowed; the payment will clear the next banking day.
              </p>
            </div>
          </div>
        )}

        {/* Resulting payments — recomputed on every change, from the same
            generator that places them on the cash-flow grid. */}
        {form.start_date && preview.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-palette-gray-divider bg-palette-gray-subtle">
            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-palette-gray-secondary">
                Next payments · showing {Math.min(preview.length, PREVIEW_ROWS)}
              </span>
              <span className="text-xs text-palette-gray-secondary">
                first {PREVIEW_DAYS / 7} weeks
              </span>
            </div>
            <div className="bg-white">
              {preview.slice(0, PREVIEW_ROWS).map((occ) => {
                const [y, m, d] = occ.date.split("-").map(Number);
                const reason = nonBankingReason(new Date(y, m - 1, d));
                return (
                  <div
                    key={occ.date}
                    className={`flex items-center justify-between gap-3 border-t border-palette-gray-divider px-4 py-2 text-sm ${
                      reason ? "bg-palette-amber-soft" : ""
                    }`}
                    title={reason ? `Falls on ${reason}` : undefined}
                  >
                    <span
                      className={`flex items-center gap-2 ${
                        reason
                          ? "font-medium text-palette-amber-primary"
                          : "text-palette-gray-heading"
                      }`}
                    >
                      {fmtPreviewDate(occ.date)}
                      {reason && <span className="text-xs font-normal">({reason})</span>}
                      {occ.movedFrom && (
                        <span className="text-xs font-normal text-palette-gray-secondary">
                          moved from {fmtPreviewDate(occ.movedFrom)}
                        </span>
                      )}
                    </span>
                    <span
                      className={`tabular-nums font-medium ${
                        reason ? "text-palette-amber-primary" : "text-palette-gray-primary"
                      }`}
                    >
                      -{formatMoney(Math.abs(occ.amount))}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <Label>Notes</Label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes")(e.target.value)}
            placeholder="Add a memo, approval reference, or context…"
            className={`${FIELD_CLS} resize-none`}
          />
        </div>

        {/* Status */}
        <div>
          <Label>Status</Label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => {
              const selected = form.status === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => chooseStatus(opt.value)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    selected
                      ? "border-palette-indigo-border bg-palette-indigo-soft text-palette-indigo-text"
                      : "border-palette-gray-divider bg-white text-palette-gray-secondary hover:bg-palette-gray-subtle"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      selected ? "bg-palette-indigo-badge" : "bg-palette-gray-placeholder"
                    }`}
                  />
                  {opt.label}
                </button>
              );
            })}
            {/* Only when there is something to delete AND the caller allows it. */}
            {isEdit && onDelete && (
              <button
                type="button"
                onClick={() => chooseStatus(DELETE_ACTION)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  confirmingDelete
                    ? "border-palette-rose-border bg-palette-rose-soft text-palette-rose-text"
                    : "border-palette-gray-divider bg-white text-palette-rose-text hover:bg-palette-rose-soft"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    confirmingDelete ? "bg-palette-rose-badge" : "bg-palette-rose-badge/50"
                  }`}
                />
                Delete
              </button>
            )}
          </div>

          {/* Cancelling: WHEN it stops is the whole decision, so the date sits
              inside the status block rather than back up in the dates row. */}
          {cancelled && !confirmingDelete && (
            <div className="mt-3 rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-4 py-3">
              <DateField
                label="Stops after"
                value={form.end_date}
                onChange={set("end_date")}
                placeholder="Pick a date"
              />
              <p className="mt-2 text-sm text-palette-gray-secondary">
                No payments after this date. Everything on or before it stays on the cash flow —
                that money already moved. To remove the payment and its whole history, delete it
                instead.
              </p>
              {droppedByCancel > 0 && (
                <p className="mt-2 text-sm font-medium text-palette-amber-primary">
                  Backdating this drops {droppedByCancel}{" "}
                  {droppedByCancel === 1 ? "payment" : "payments"} that have already come due off
                  the cash flow.
                </p>
              )}
            </div>
          )}

          {/* Delete confirmation. Inline rather than a second dialog stacked on
              this one, and it says what goes with the row: the exceptions and
              cleared-payment confirmations cascade. */}
          {confirmingDelete && (
            <div className="mt-3 rounded-xl border border-palette-rose-border bg-palette-rose-soft px-4 py-3">
              <p className="text-sm font-semibold text-palette-rose-text">
                Delete this scheduled payment permanently?
              </p>
              <p className="mt-1 text-sm text-palette-rose-text">
                The record disappears everywhere, along with every date change and cleared-payment
                confirmation recorded against it — past occurrences included. This cannot be undone.
                To stop future payments but keep the history, pick Cancelled instead.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleting}
                  onClick={() => onDelete?.()}
                  className="font-semibold"
                >
                  {deleting ? "Deleting…" : "Delete permanently"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={deleting}
                  onClick={() => setConfirmingDelete(false)}
                >
                  Keep it
                </Button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="rounded-xl border border-palette-rose-border bg-palette-rose-soft px-4 py-3 text-sm text-palette-rose-text">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
